import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	IconButton,
	Button,
	Divider,
	Stack,
	Grid,
	Chip,
	Box,
	Tooltip,
	useTheme,
	alpha,
	Theme,
} from "@mui/material";
import { Close, ReceiptLong, Cancel, Visibility, AttachMoney, CheckCircle, HourglassEmpty } from "@mui/icons-material";
import { format } from "date-fns";
import { ComissionStatusData } from "../../../../../shared/services/api/comission/ComissionServices";
import { globalFormatCurrencyBRL, globalTranslateStatus, globalTranslate, globalTranslateStatusComission, globalFormatDates } from "../../../../../shared/utils"; // globalTranslate importado

// ==========================================================
// INTERFACE BASE (Mantida)
// ==========================================================

interface ComissionRowData extends Omit<ComissionStatusData, "dueDate" | "createdAt" | "reason"> {
	createdAt: string | null;
	dueDate: string | null;
	reason: string | null;
	description: string;
	obs: string | null;
	nfseId: string | null;
	type: string;
	userBonus: string | null;
	proposal: { certificateNumber: number };
}

interface ModalInstallmentComissionDetailsProps {
	open: boolean;
	onClose: () => void;
	row: ComissionRowData;
}

// Lógica de Status (Mantida)
const getStatusColors = (theme: Theme, status: string) => {
	const isDarkMode = theme.palette.mode === "dark";
	const statusKey = status.toLowerCase();

	const colors = {
		paid: { bg: isDarkMode ? alpha(theme.palette.success.main, 0.1) : "#f0fdf4", color: theme.palette.success.main, icon: <CheckCircle /> },
		preview: { bg: isDarkMode ? alpha(theme.palette.warning.main, 0.1) : "#fffbe6", color: theme.palette.warning.main, icon: <HourglassEmpty /> },
		canceled: { bg: isDarkMode ? alpha(theme.palette.error.main, 0.1) : "#fef2f2", color: theme.palette.error.main, icon: <Cancel /> },
		billed: { bg: isDarkMode ? alpha(theme.palette.info.main, 0.1) : "#eef2ff", color: theme.palette.info.main, icon: <ReceiptLong /> },
		transferred: { bg: isDarkMode ? alpha(theme.palette.info.dark, 0.1) : "#e6fffa", color: theme.palette.info.dark, icon: <AttachMoney /> },
	};

	return (
		colors[statusKey as keyof typeof colors] || {
			bg: isDarkMode ? theme.palette.grey[800] : theme.palette.grey[50],
			color: theme.palette.grey[500],
			icon: <Visibility />,
		}
	);
};

export const ModalInstallmentComissionDetails = ({ open, onClose, row }: ModalInstallmentComissionDetailsProps) => {
	const theme = useTheme();
	const currentStatus = getStatusColors(theme as Theme, row.status);
	const isDarkMode = theme.palette.mode === "dark";

	const isCancelled = row.status.toLowerCase() === "canceled";

	// Tradução do tipo de Provento
	const getComissionLabel = (type: string | undefined) => {
		const lowerType = type?.toLowerCase();
		if (lowerType === "prize") return "Premiação (Prêmio)";
		if (lowerType === "bonus") return "Bônus";
		return type || "Comissão";
	};

	// Estilos de fundo adaptáveis
	const obsBgColor = isDarkMode ? alpha(theme.palette.grey[700], 0.5) : "#f5f5f5";
	const descBgColor = isDarkMode ? theme.palette.grey[800] : "#f5f5f5"; // Fundo da descrição mais visível no Dark Mode

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					pb: 1,
					backgroundColor: theme.palette.background.paper,
				}}
			>
				<Typography fontWeight={400} variant="h6" color="primary.main">
					Detalhes da Comissão
				</Typography>
				<IconButton onClick={onClose}>
					<Close />
				</IconButton>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ px: 4, pt: 3, pb: 2 }}>
				<Stack spacing={2}>
					{/* Título do Provento + Valor */}
					<Stack direction="row" justifyContent="space-between" alignItems="center">
						<Box>
							<Typography variant="body2" color="text.secondary">
								Provento
							</Typography>
							<Typography variant="body1" fontWeight={600}>
								{getComissionLabel(row.type)}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								Certificado: #{row.proposal?.certificateNumber || "N/A"}
							</Typography>
						</Box>

						<Box textAlign="right">
							<Typography variant="body2" color="text.secondary">
								Valor
							</Typography>
							<Typography variant="h6" fontWeight={600} color={parseFloat(row.value) < 0 ? "error.main" : "success.main"}>
								{globalFormatCurrencyBRL(row.value)}
							</Typography>
						</Box>
					</Stack>

					<Divider />

					{/* Criação + Vencimento / Data de Projeção */}
					<Stack direction="row" justifyContent="space-between" alignItems="center">
						<Box>
							<Typography variant="body2" color="text.secondary">
								Criada em
							</Typography>
							<Typography variant="body1">{row.createdAt ? format(new Date(row.createdAt), "dd/MM/yyyy") : "-"}</Typography>
						</Box>

						<Box textAlign="right">
							<Typography variant="body2" color="text.secondary">
								{row.dueDate ? "Vencimento" : "Projeção"}
							</Typography>
							<Typography variant="body1">{row.dueDate ? format(new Date(row.dueDate), "dd/MM/yyyy") : "-"}</Typography>
						</Box>
					</Stack>

					{/* Repasse sobre Premiação — exibido apenas quando type === "prize" */}
					{row.type?.toLowerCase() === "prize" && row.userBonus && parseFloat(row.userBonus) > 0 && (
						<>
							<Stack direction="row" justifyContent="space-between" alignItems="center">
								<Box>
									<Typography variant="body2" color="text.secondary">
										Repasse sobre Premiação
									</Typography>
									<Typography variant="h6" fontWeight={600} color="info.main">
										{globalFormatCurrencyBRL(row.userBonus)}
									</Typography>
								</Box>
							</Stack>
							<Divider />
						</>
					)}

					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Typography variant="body2" color="text.secondary" gutterBottom>
								Descrição
							</Typography>
							<Box
								sx={{
									backgroundColor: descBgColor, // Fundo cinza adaptável
									borderRadius: 1,
									p: 1.5,
								}}
							>
								<Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
									{row.description}
								</Typography>
							</Box>
						</Grid>

						<Grid item xs={6}>
							<Typography variant="body2" color="text.secondary">
								Status
							</Typography>
							<Chip
								size="small"
								icon={currentStatus.icon}
								label={globalTranslateStatusComission(row.status)}
								sx={{
									backgroundColor: currentStatus.color + "22",
									color: currentStatus.color,
									fontWeight: 500,
								}}
							/>
						</Grid>
					</Grid>

					{/* Nota Fiscal */}
					{row.nfseId && (
						<Box>
							<Typography variant="body2" color="text.secondary">
								Nota Fiscal
							</Typography>
							<Chip size="small" icon={<ReceiptLong fontSize="small" />} label="Emitida" color="success" sx={{ fontWeight: 500 }} />
						</Box>
					)}

					{/* Motivo do cancelamento */}
					{isCancelled && row.reason && (
						<Box>
							<Typography variant="subtitle2" color="error.main" >
								Cancelado em: {globalFormatDates(row.cancelDate)}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Motivo do Cancelamento
							</Typography>
							<Typography variant="body1" color="error.main" fontStyle="italic">
								{row.reason}
							</Typography>
						</Box>
					)}

					{/* Observações com fundo leve */}
					{row.obs && (
						<Box
							sx={{
								backgroundColor: obsBgColor,
								borderRadius: 2,
								p: 2,
								mt: 1,
							}}
						>
							<Typography variant="subtitle2" color="text.secondary" gutterBottom>
								Observações
							</Typography>
							<Typography variant="body2">{row.obs}</Typography>
						</Box>
					)}
				</Stack>
			</DialogContent>

			<DialogActions sx={{ px: 4, pb: 3, pt: 1 }}>
				<Grid container justifyContent="flex-end" alignItems="center">
					<Grid item>
						<Button variant="contained" color="primary" onClick={onClose}>
							Fechar
						</Button>
					</Grid>
				</Grid>
			</DialogActions>
		</Dialog>
	);
};
